/* ---------------------------------------------------------------------------
   STL 2026 — Sort: a real dropdown, built from Madrid's own <select>

   Serena, 7 Sep 2026. The reference is assemblylabel.com: "SORT" with a chevron,
   opening a short right-aligned list of options rather than a native select.

   WHY THIS IS A BUILD AND NOT A SETTING. Two things were checked first (rule 2).
   Madrid's schema has only `enable_sorting`, a boolean — there is no option list.
   And the options themselves come from `results.sort_options`, which is Shopify's
   own fixed set; nothing in the admin or in Search & Discovery curates it. So
   condensing nine options to five cannot be done from the editor.

   WHY IT IS BUILT FROM THE <select> RATHER THAN NEW MARKUP. 3.4 deliberately left
   the native select in place at `opacity: 0` rather than hiding it, because
   `display: none` would have killed the native dropdown, the accessible name from
   `<label for="SortBy">` and keyboard focus all at once. Replacing it outright
   would give that problem straight back. Instead the select stays the source of
   truth: this reads the options out of it, and choosing one writes back to the
   select and dispatches `change`, so Madrid's own `<facet-filters-form>` handler
   does the fetching exactly as before. No core edit, and with JS off the select
   is still a working form control.

   ORDER COMES FROM THE ALLOW-LIST, not from Shopify's order, so the menu reads
   the way the reference does regardless of what the platform returns.
   ------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* Matched against the option VALUE, which is Shopify's stable sort key —
     the visible names are locale strings and would break in translation. */
  var ALLOW = [
    { value: 'manual',          label: 'Featured' },
    { value: 'best-selling',    label: 'Best Selling' },
    { value: 'price-ascending', label: 'Price: Low to High' },
    { value: 'price-descending', label: 'Price: High to Low' },
    { value: 'created-descending', label: 'Newest' }
  ];

  function build(wrapper) {
    var select = wrapper.querySelector('select[name="sort_by"]');
    if (!select) return;
    /* GUARD ON THE MARKUP, NOT A FLAG. Madrid re-renders the facets form by
       replacing the wrapper's INNER html — the wrapper element itself survives.
       A `data-` flag on it therefore outlives the toggle and menu it was meant
       to track, so a flag-based guard reports "already built" over an empty
       wrapper and the control never comes back. Found the moment the in-stock
       auto-apply started firing a re-render on load. */
    if (wrapper.querySelector('.stl-sort__toggle')) return;

    var available = {};
    Array.prototype.forEach.call(select.options, function (o) { available[o.value] = o; });

    var items = ALLOW.filter(function (a) { return available[a.value]; });
    /* Nothing recognised — a theme update could rename the keys. Leave Madrid's
       select exactly as it was rather than shipping an empty menu. */
    if (items.length < 2) return;

    var menu = document.createElement('div');
    menu.className = 'stl-sort__menu';
    menu.hidden = true;

    var list = document.createElement('ul');
    list.className = 'stl-sort__list';

    items.forEach(function (a) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'stl-sort__option';
      btn.textContent = a.label;
      btn.dataset.value = a.value;
      btn.setAttribute('aria-current', String(select.value === a.value));
      li.appendChild(btn);
      list.appendChild(li);
    });

    menu.appendChild(list);

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'stl-sort__toggle';
    toggle.setAttribute('aria-expanded', 'false');
    /* No chevron — Serena, 7 Sep 2026. The desktop frame `5227:5617` draws the
       words alone, and the mobile bar `5886:7182` does the same. */
    toggle.innerHTML = '<span class="stl-sort__label"></span>';
    /* The visible label is the same string Madrid's own <label> carries, so the
       two never drift apart. */
    var native = wrapper.querySelector('label[for="SortBy"], .facets-sorting__label');
    toggle.querySelector('.stl-sort__label').textContent =
      (native ? native.textContent : 'Sort By').trim();

    wrapper.classList.add('stl-sort');
    wrapper.appendChild(toggle);
    wrapper.appendChild(menu);

    function close() {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    function open() {
      menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }

  /* SUBMIT WITHOUT MADRID'S 500ms DEBOUNCE.

     `facets.js` binds the form's `input` listener as
     `debounce((e) => this.onSubmitHandler(e), 500)`, so dispatching `input` —
     which is what this menu used to do — parks every sort behind a flat half
     second before the fetch is even issued. Measured on New Arrivals: click at
     t=0, request out at t≈900ms, response 770ms, so roughly **half the visible
     lag was dead time**. The debounce is there so typing in the price fields
     does not fire a request per keystroke; a sort is a single discrete choice
     and has nothing to debounce.

     So the handler is called directly. `syncFacetInputs` has to be called too,
     because it is the OTHER listener on that same `input` event — it mirrors the
     choice into the mobile sort form, and `onSubmitHandler` merges FormData from
     every facet form, so skipping it would let a stale `sort_by` from the mobile
     form win (`singleValueKeys` uses `.set()`, so the last form parsed wins).

     `onSubmitHandler` reads `preventDefault`, `srcElement.className` and
     `target`, and `renderFilters` later does `event.target.closest('.js-filter')`
     — so the synthetic event carries the real <select> as both, which resolves
     to null exactly as the genuine event did.

     **Falls back to the old dispatch** if Madrid renames either entry point, so
     the worst case is the previous behaviour rather than a dead control. */
  function submit(select) {
    /* Dim the grid immediately — see `stl-facets.js` for why Madrid's own
       `.loading` class cannot carry this. */
    if (typeof window.stlFacetsBusy === 'function') window.stlFacetsBusy();

    var host = select.closest('facet-filters-form');
    var FFF = window.FacetFiltersForm;

    if (host && typeof host.onSubmitHandler === 'function') {
      if (FFF && typeof FFF.syncFacetInputs === 'function') {
        try { FFF.syncFacetInputs({ target: select }); } catch (e) {}
      }
      host.onSubmitHandler({
        preventDefault: function () {},
        srcElement: select,
        target: select
      });
      return;
    }

    /* MADRID LISTENS FOR `input`, NOT `change` — a real click on a <select> fires
       both, so dispatching only `change` looked correct and did nothing. */
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (menu.hidden) { open(); } else { close(); }
    });

    list.addEventListener('click', function (e) {
      var btn = e.target.closest('.stl-sort__option');
      if (!btn) return;
      e.preventDefault();
      select.value = btn.dataset.value;
      submit(select);
      Array.prototype.forEach.call(list.querySelectorAll('.stl-sort__option'), function (b) {
        b.setAttribute('aria-current', String(b === btn));
      });
      close();
    });

    document.addEventListener('click', function (e) {
      if (menu.hidden) return;
      if (wrapper.contains(e.target)) return;
      close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !menu.hidden) { close(); toggle.focus(); }
    });
  }

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.facet-filters.facets-sorting'),
      build
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Madrid re-renders the whole facets form on a filter change, which takes the
     menu with it. The flag lives on the wrapper, so a fresh one rebuilds and an
     untouched one is skipped. */
  var host = document.querySelector('.facets-container');
  if (host && window.MutationObserver) {
    new MutationObserver(init).observe(host, { childList: true, subtree: true });
  }
})();
